import * as PIXI from 'pixi.js';
import { game, pauseGame, unpauseGame } from "js/main";
import { Layout } from 'js/layouts/GameEndPopup';
import { Lerper } from 'framework/utils/Lerper';
import { Range } from 'framework/utils/Range';
import { waitForMs } from 'framework/utils/async-utils';
import { TweenManager } from 'framework/TweenManager/TweenManager';
import { showInterstitial } from "js/sdk/showInterstitial";
import { GameOverLeaderboardPopup } from "./GameOverLeaderboardPopup";
import TextInput from "js/framework/extends/TextInput";
import { saveUserData } from "js/sdk/saveUserData";
import { loadUserData } from "js/sdk/loadUserData";
import { Game } from 'framework/Game';

interface IGameOverPopupConfig {
  parent: PIXI.Container;
  tweenMgr: TweenManager;
  restart: () => unknown;
  score: number;
  highscore: number;
}

export class GameOverPopup {
  private game: Game;
  private readonly layout: Layout;
  private readonly lerper: Lerper;
  private readonly tweenMgr: TweenManager;
  
  private readonly nameInput: TextInput;
  private readonly score: number;

  constructor({
    parent,
    tweenMgr,
    score,
    highscore,
    restart,
  }: IGameOverPopupConfig) {
    this.game = game;
    this.tweenMgr = tweenMgr;
    this.score = score;
    
    this.layout = new Layout();
    parent.addChild(this.layout.root);

    const {
      inputField,
      inputSprite,
      inputLabel,
      continueButton,
      submitButton,
      highscoreText,
      scoreLabel,
      root,
    } = this.layout;

    this.nameInput = new TextInput(
      inputField,
      inputLabel,
      game.getLocalized("Enter Your Name"),
      8,
    );
    loadUserData(['username'])
      .then(({ username }) => this.nameInput.value = username);


    this.lerper = new Lerper({
      initValue: -1,
      lerp: 0.1,
      snap: 0.5,
      autoUpdate: true,
      range: new Range(0, Infinity),
      destroySignal: root.onDestroyed,
    });

    inputSprite.width = continueButton.width;
    inputSprite.height = continueButton.height;

    continueButton.visible = false;
    submitButton.visible = false;
    inputField.visible = false;

    if (highscore) {
      highscoreText.text = highscoreText.text
        .replace('%%%', highscore.toString());
    } else {
      highscoreText.visible = false;
    }

    const defaultScoreLabel = scoreLabel.text;
    this.lerper.signals.onValueUpdate.add(async (value) => {
      const rounded = Math.round(value);
      scoreLabel.text = defaultScoreLabel.replace('%%%', rounded.toString());
      if (rounded === this.score) this.showContinueButton(restart);
    });

    this.showPopup();
  }

  private async showPopup() {

    waitForMs(1000).then(() => this.lerper.setTarget(this.score));

    this.tweenMgr.create(this.layout.root)
      .from({alpha: 0}, 300, this.tweenMgr.easing.Sinusoidal.Out, true);
  }

  private async hidePopup() {
    await this.tweenMgr.create(this.layout.root)
      .to({alpha: 0}, 400, this.tweenMgr.easing.Sinusoidal.Out, true)
      .asyncOnComplete();

    this.layout.root.destroy();
  }

  private async showContinueButton(restart: () => unknown): Promise<void> {
    const {
      inputField,
      continueButton,
      submitButton,
    } = this.layout;

    await Promise.all([
      inputField,
      continueButton,
      submitButton,
    ].map(el => {
      el.visible = true;
      return this.tweenMgr.create(el)
        .from({alpha: 0}, 300, this.tweenMgr.easing.Sinusoidal.Out, true)
        .asyncOnComplete();
    }));

    continueButton.once(
      'pointerdown',
      async () => {
        this.game.sounds.play("button_click");
        this.nameInput.blur();
        await this.hidePopup();
        pauseGame();
        await showInterstitial();
        unpauseGame();
        restart();
      },
    );

    submitButton.on(
      'pointerdown',
      () => {
        this.game.sounds.play("button_click");
        this.nameInput.blur();
        this.submitScore(restart)
      }
    );
  }

  private async submitScore(restart: () => unknown) {
    if (!this.nameInput.isValid()) {
      this.nameInput.focus();
      return;
    }

    const parent = this.layout.root.parent;

    await this.hidePopup();

    const username = this.nameInput.value;
    saveUserData({ username });

    new GameOverLeaderboardPopup({
      parent,
      restart,
      username,
      tweenMgr: this.tweenMgr,
      score: this.score,
    });
  }

  public getRoot(): PIXI.Container {
    return this.layout.root;
  }
}